// src/features/records/hooks/useRecordDetail.js
import { useContext } from 'react';
import RecordDetailContext from '../contexts/RecordDetailContext';

/**
 * Hook to access RecordDetail context.
 * Provides methods to open/close record detail modal and access current state.
 * 
 * @returns {Object} RecordDetail context value
 * @returns {Object|null} selectedRecord - The currently selected record
 * @returns {boolean} isOpen - Whether the detail modal is open
 * @returns {string} mode - Display mode ('modal' or 'page')
 * @returns {Function} openDetail - Function to open record detail
 * @returns {Function} closeDetail - Function to close record detail
 * @returns {Function} editRecord - Function to edit a record
 * @returns {Function} deleteRecord - Function to delete a record
 * 
 * @throws {Error} If used outside of RecordDetailProvider
 * 
 * @example
 * const { openDetail, closeDetail, isOpen, editRecord, deleteRecord } = useRecordDetail();
 * 
 * // Open a record detail
 * openDetail(record);
 * 
 * // Open with specific mode
 * openDetail(record, { mode: 'page' });
 * 
 * // Edit a record
 * editRecord(record);
 * 
 * // Delete a record
 * deleteRecord(record);
 */
const useRecordDetail = () => {
  const context = useContext(RecordDetailContext);
  
  if (!context) {
    throw new Error(
      'useRecordDetail must be used within a RecordDetailProvider. ' +
      'Make sure your component is wrapped with RecordDetailProvider.'
    );
  }
  
  return context;
};

export default useRecordDetail;